import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AutoComplete } from '@xing-com/auto-complete';
import type {
  AutocompletionLocationsCitySuggestion,
  AutocompletionProfileJobRoleSuggestion,
} from '@xing-com/crate-common-graphql-types';
import {
  useJobRoleSuggestions,
  useLocationSuggestions,
} from '@xing-com/crate-lebenslauf-hooks';
import { Toggle } from '@xing-com/toggle';
import { Headline } from '@xing-com/typography';

import type { OnSaveProps } from '../preferences';

import {
  StyledSubmitButton,
  // StyledBackButton,
  StyledLabel,
  StyledHr,
  StyledInfo,
  StyledHeadline,
  StyledIllustration,
  StyledTagContainer,
  StyledTag,
} from './job-preferences.styled';

type JobPreferencesProps = {
  onSave: ({ locations, targetPosition, homeOffice }: OnSaveProps) => void;
  onBack: () => void;
};

type Location = {
  cityId?: number | null;
  cityIdWithScrambling?: string | null;
  cityName?: string | null;
};

export const JobPreferences: React.FC<JobPreferencesProps> = ({
  onSave,
  // onBack,
}) => {
  const [city, setCity] = useState('');
  const [targetPosition, setTargetPosition] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [homeOffice, setHomeOffice] = useState(false);
  const intl = useIntl();
  const { data: locationData, refetch: refetchLocation } =
    useLocationSuggestions(city);
  const { data: jobRoleData, refetch: refetchJobRole } =
    useJobRoleSuggestions(targetPosition);
  const citySuggestions = (
    locationData?.autocompletionLocationsCity?.collection || []
  ).filter(
    (item): item is AutocompletionLocationsCitySuggestion => item !== null
  );
  const jobRoleSuggestions =
    jobRoleData?.autocompletionProfileJobRole?.collection || [];
  const handleOnCityChange = (value: string): void => {
    setCity(value);
  };
  const handleOnJobRoleChange = (value: string): void => {
    setTargetPosition(value);
  };
  const handleOnSelect = (
    item: AutocompletionLocationsCitySuggestion
  ): void => {
    setLocations([
      ...locations,
      {
        cityName: item.name,
        cityId: item.cityId,
        cityIdWithScrambling: item.cityIdWithScrambling,
      },
    ]);
    setCity('');
  };
  const handleOnDelete = (cityIndex: number): void => {
    setLocations(locations.filter((_, index) => index !== cityIndex));
  };
  // const handleOnBack = (event: React.MouseEvent): void => {
  //   event.preventDefault();

  //   if (event.pageY) {
  //     onBack();
  //   }
  // };
  const handleOnSave = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    onSave({ locations, targetPosition, homeOffice });
  };

  useEffect(() => {
    if (city) {
      refetchLocation(city);
    }
    if (targetPosition) {
      refetchJobRole(targetPosition);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [city, targetPosition]);

  return (
    <>
      <StyledHeadline size="large" sizeConfined="xxlarge">
        <StyledIllustration size="medium" />
        <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_TITLE" />
      </StyledHeadline>
      <form onSubmit={handleOnSave}>
        <Headline size="medium" sizeConfined="large">
          <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_CITY_TITLE" />
        </Headline>
        <AutoComplete
          variant="inputBar"
          value={city}
          placeholder={intl.formatMessage({
            id: 'CRATE_VITA_PREFERENCES_SETTINGS_CITY_PLACEHOLDER',
          })}
          label={intl.formatMessage({
            id: 'CRATE_VITA_PREFERENCES_SETTINGS_CITY_PLACEHOLDER',
          })}
          onChange={handleOnCityChange}
          onSelect={(item) => handleOnSelect(item)}
          suggestionToString={(
            item: AutocompletionLocationsCitySuggestion | null
          ) => item?.name || ''}
          id="city-auto-complete"
          name="city-auto-complete"
          disabled={locations?.length >= 3}
          suggestions={citySuggestions}
        />
        <StyledTagContainer>
          {locations?.map((tag: Location, index) => (
            <StyledTag
              selected
              key={tag.cityName}
              variant="add"
              onClick={() => handleOnDelete(index)}
            >
              {tag.cityName}
            </StyledTag>
          ))}
        </StyledTagContainer>
        <StyledInfo size="small">
          <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_CITY_HINT" />
        </StyledInfo>
        <StyledLabel>
          <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_HOME_OFFICE" />
          <Toggle on={homeOffice} onChange={() => setHomeOffice(!homeOffice)} />
        </StyledLabel>
        <StyledHr />
        <Headline size="medium" sizeConfined="large">
          <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_JOBNAME_TITLE" />
        </Headline>
        <AutoComplete
          variant="inputBar"
          value={targetPosition}
          suggestions={jobRoleSuggestions}
          placeholder={intl.formatMessage({
            id: 'CRATE_VITA_PREFERENCES_SETTINGS_JOBNAME_PLACEHOLDER',
          })}
          label={intl.formatMessage({
            id: 'CRATE_VITA_PREFERENCES_SETTINGS_JOBNAME_PLACEHOLDER',
          })}
          onChange={handleOnJobRoleChange}
          onSelect={(item: AutocompletionProfileJobRoleSuggestion) =>
            setTargetPosition(item.suggestion || '')
          }
          suggestionToString={(
            item: AutocompletionProfileJobRoleSuggestion | null
          ) => item?.suggestion || ''}
          id="jobrole-auto-complete"
          name="jobrole-auto-complete"
        />
        <StyledInfo size="small">
          <FormattedMessage id="CRATE_VITA_PREFERENCES_SETTINGS_JOBNAME_HINT" />
        </StyledInfo>
        {/* <StyledBackButton
          onClick={handleOnBack}
          variant="secondary"
          type="text">
          <FormattedMessage id="CRATE_VITA_PREFERENCES_BACK" />
        </StyledBackButton> */}
        <StyledSubmitButton
          variant="primary"
          type="submit"
          disabled={(locations.length === 0 && !homeOffice) || !targetPosition}
        >
          <FormattedMessage id="CRATE_VITA_PREFERENCES_DONE" />
        </StyledSubmitButton>
      </form>
    </>
  );
};
